import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    createPageComment,
    deletePageComment,
    addReplyToComment,
    resolveComment,
    updatePageComment,
    setActiveMarkId,
    clearPendingMark,
} from '../../redux/Slices/pageCommentsSlice';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import { Check, MoreHorizontal, Trash2, Pencil, RotateCcw, Send } from 'lucide-react';
import { showError, getErrorMessage } from '../../utils/snackbarHelpers';
import { renderMarkdown } from '../../utils/sanitizeHtml';

const formatTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const d = new Date(date);
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CommentItem = ({ item, pageId, canComment, isAuthor, onEdit }) => {
    const dispatch = useDispatch();
    const [showMenu, setShowMenu] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(item.comment || '');
    const menuRef = useRef(null);

    useEffect(() => {
        if (!showMenu) return;
        const handleClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showMenu]);

    const handleDelete = async () => {
        setShowMenu(false);
        try {
            await dispatch(deletePageComment({ pageId, commentId: item._id })).unwrap();
        } catch (error) {
            showError(dispatch, getErrorMessage(error, 'Failed to delete comment.'));
        }
    };

    const handleSaveEdit = async () => {
        if (editText.trim() && editText !== item.comment) {
            try {
                await dispatch(updatePageComment({ pageId, commentId: item._id, comment: editText.trim() })).unwrap();
            } catch (error) {
                showError(dispatch, getErrorMessage(error, 'Failed to save your edit.'));
                return; // keep the editor open so the user can retry
            }
        }
        setIsEditing(false);
    };

    const handleEditKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSaveEdit();
        }
        if (e.key === 'Escape') {
            setIsEditing(false);
            setEditText(item.comment || '');
        }
    };

    const member = item.member || {};

    return (
        <div className="pct-comment-item">
            <div className="pct-comment-header">
                <div className="pct-comment-avatar">
                    {member.profilePicture ? (
                        <img src={member.profilePicture} alt={member.name} />
                    ) : (
                        <ImagePlaceholder name={member.name || 'U'} size={20} />
                    )}
                </div>
                <div className="pct-comment-meta">
                    <span className="pct-comment-author">{member.name || 'Unknown'}</span>
                    <span className="pct-comment-time">{formatTimeAgo(item.createdAt)}</span>
                </div>
                {isAuthor && canComment && (
                    <div className="pct-comment-actions" ref={menuRef}>
                        <button
                            className="pct-action-btn"
                            onClick={() => setShowMenu(!showMenu)}
                        >
                            <MoreHorizontal size={14} />
                        </button>
                        {showMenu && (
                            <div className="pct-action-menu">
                                <button onClick={() => { setIsEditing(true); setShowMenu(false); }}>
                                    <Pencil size={13} />
                                    <span>Edit</span>
                                </button>
                                <button className="danger" onClick={handleDelete}>
                                    <Trash2 size={13} />
                                    <span>Delete</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="pct-comment-body">
                {isEditing ? (
                    <div className="pct-edit-input">
                        <textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            onKeyDown={handleEditKeyDown}
                            autoFocus
                            rows={2}
                        />
                        <div className="pct-edit-actions">
                            <button className="pct-edit-cancel" onClick={() => { setIsEditing(false); setEditText(item.comment || ''); }}>
                                Cancel
                            </button>
                            <button className="pct-edit-save" onClick={handleSaveEdit}>
                                Save
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="page-comment-content" dangerouslySetInnerHTML={{ __html: renderMarkdown(item.comment) }} />
                )}
            </div>
        </div>
    );
};

const PageCommentThread = ({ comment, pageId, isActive, canComment, isResolved, isPending }) => {
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.user);
    const pendingMark = useSelector(state => state.pageComments.pendingMark);
    const currentUserId = user?.user?._id || user?.user?.id;

    const [replyText, setReplyText] = useState('');
    const [showReplyInput, setShowReplyInput] = useState(false);
    const [newCommentText, setNewCommentText] = useState('');
    const replyInputRef = useRef(null);
    const newCommentRef = useRef(null);

    // Auto-focus reply input
    useEffect(() => {
        if (showReplyInput && replyInputRef.current) {
            replyInputRef.current.focus();
        }
    }, [showReplyInput]);

    // Auto-focus new comment input
    useEffect(() => {
        if (isPending && newCommentRef.current) {
            newCommentRef.current.focus();
        }
    }, [isPending]);

    const handleReply = async () => {
        if (!replyText.trim()) return;
        const text = replyText.trim();
        try {
            await dispatch(addReplyToComment({
                pageId,
                commentId: comment._id,
                comment: text
            })).unwrap();
            setReplyText('');
            setShowReplyInput(false);
        } catch (error) {
            // Keep the draft so the user doesn't lose what they typed.
            showError(dispatch, getErrorMessage(error, 'Failed to post your reply.'));
        }
    };

    const handleReplyKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleReply();
        }
        if (e.key === 'Escape') {
            setShowReplyInput(false);
            setReplyText('');
        }
    };

    const handleNewComment = async () => {
        if (!newCommentText.trim() || !pendingMark) return;
        const text = newCommentText.trim();
        try {
            await dispatch(createPageComment({
                pageId,
                comment: text,
                markId: pendingMark.markId,
                quotedText: pendingMark.quotedText || ''
            })).unwrap();
            setNewCommentText('');
        } catch (error) {
            // Keep the draft so the user can retry without retyping.
            showError(dispatch, getErrorMessage(error, 'Failed to post your comment.'));
        }
    };

    const handleNewCommentKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleNewComment();
        }
        if (e.key === 'Escape') {
            dispatch(clearPendingMark());
            dispatch(setActiveMarkId(null));
        }
    };

    const handleResolve = async () => {
        try {
            await dispatch(resolveComment({ pageId, commentId: comment._id })).unwrap();
        } catch (error) {
            showError(dispatch, getErrorMessage(error, 'Failed to resolve the comment.'));
        }
    };

    const handleClick = () => {
        dispatch(setActiveMarkId(comment.markId));
    };

    const replies = comment.replies || [];

    // If this is a pending new thread (no saved comment yet), show just the input
    if (isPending && pendingMark) {
        return (
            <div
                className={`pct-thread ${isActive ? 'active' : ''} new-thread`}
                data-mark-id={comment.markId}
            >
                {comment.quotedText && (
                    <div className="pct-quoted-text">
                        "{comment.quotedText}"
                    </div>
                )}
                <div className="pct-new-comment-input">
                    <textarea
                        ref={newCommentRef}
                        placeholder="Add a comment..."
                        value={newCommentText}
                        onChange={(e) => setNewCommentText(e.target.value)}
                        onKeyDown={handleNewCommentKeyDown}
                        rows={2}
                        autoFocus
                    />
                    <button
                        className="pct-submit-btn"
                        onClick={handleNewComment}
                        disabled={!newCommentText.trim()}
                    >
                        <Send size={14} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`pct-thread ${isActive ? 'active' : ''} ${isResolved ? 'resolved' : ''}`}
            data-mark-id={comment.markId}
            onClick={handleClick}
        >
            {/* Quoted text snippet */}
            {comment.quotedText && (
                <div className="pct-quoted-text">
                    "{comment.quotedText}"
                </div>
            )}

            {/* Main comment */}
            <CommentItem
                item={comment}
                pageId={pageId}
                canComment={canComment}
                isAuthor={comment.member?._id === currentUserId || comment.member?.id === currentUserId}
            />

            {/* Replies */}
            {replies.length > 0 && (
                <div className="pct-replies">
                    {replies.map(reply => (
                        <CommentItem
                            key={reply._id}
                            item={reply}
                            pageId={pageId}
                            canComment={canComment}
                            isAuthor={reply.member?._id === currentUserId || reply.member?.id === currentUserId}
                        />
                    ))}
                </div>
            )}

            {/* Thread actions */}
            <div className="pct-thread-footer">
                {canComment && !isResolved && (
                    <>
                        {showReplyInput ? (
                            <div className="pct-reply-input">
                                <textarea
                                    ref={replyInputRef}
                                    placeholder="Reply..."
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    onKeyDown={handleReplyKeyDown}
                                    rows={1}
                                />
                                <button
                                    className="pct-submit-btn"
                                    onClick={handleReply}
                                    disabled={!replyText.trim()}
                                >
                                    <Send size={14} />
                                </button>
                            </div>
                        ) : (
                            <div className="pct-footer-actions">
                                <button
                                    className="pct-reply-btn"
                                    onClick={(e) => { e.stopPropagation(); setShowReplyInput(true); }}
                                >
                                    Reply
                                </button>
                                <button
                                    className="pct-resolve-btn"
                                    onClick={(e) => { e.stopPropagation(); handleResolve(); }}
                                    title="Resolve"
                                >
                                    <Check size={14} />
                                </button>
                            </div>
                        )}
                    </>
                )}
                {isResolved && canComment && (
                    <button
                        className="pct-reopen-btn"
                        onClick={(e) => { e.stopPropagation(); handleResolve(); }}
                    >
                        <RotateCcw size={13} />
                        <span>Reopen</span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default PageCommentThread;
